import { useEffect, useRef, useState } from "react";
import beepSound from "../static/media/fire_alarm.mp3";
import urgentSound from "../static/media/alarm2.wav";
import softSound from "../static/media/alarm.wav";
import Alert1 from "../static/media/Alert1.mp3";
import Alert2 from "../static/media/Alert2.mp3";
import Alert3 from "../static/media/Alert3.mp3";

export const ARC_ALARM_SOUNDS = [
  { label: "No Sound", value: "", filename: "no_sound" },
  { label: "Default Beep", value: beepSound, filename: "fire_alarm.mp3" },
  { label: "Alert 1", value: Alert1, filename: "Alert1.mp3" },
  { label: "Alert 2", value: Alert2, filename: "Alert2.mp3" },
  { label: "Alert 3", value: Alert3, filename: "Alert3.mp3" },
  { label: "Urgent Alarm", value: urgentSound, filename: "alarm2.wav" },
  { label: "Soft Chime", value: softSound, filename: "alarm.wav" },
];

const NO_SOUND = ARC_ALARM_SOUNDS[0];

function normalizeFilename(raw) {
  if (raw == null || raw === "") return null;
  const base = String(raw).split("/").pop() || "";
  const lower = base.toLowerCase();
  if (!lower || lower === "no_sound" || lower === "no sound" || lower === "nosound") {
    return "no_sound";
  }
  return base;
}

function resolveSoundFromPreference(selectedSoundFile) {
  const filename = normalizeFilename(selectedSoundFile);
  // API null / empty / no_sound → stay silent
  if (!filename || filename === "no_sound") return NO_SOUND;
  const found = ARC_ALARM_SOUNDS.find((s) => s.filename.toLowerCase() === filename.toLowerCase());
  // Unknown file name from API → do not invent a default alarm
  return found || NO_SOUND;
}

function shouldPlayArcAlarm(sensors = []) {
  if (!Array.isArray(sensors) || sensors.length === 0) return false;
  let maxLvl = 0;
  for (const s of sensors) {
    const level = Number(s?.arc_level) || 0;
    if (level > maxLvl) maxLvl = level;
    if (Number(s?.arc_state) === 1 || s?.arc_state === true) return true;
  }
  // Same threshold as classic dashboard: warning and above
  return maxLvl > 1;
}

/**
 * Plays the Settings-selected alarm while any sensor is in arc / elevated arc level.
 * Returns { audioRef, selectedSound } — render a hidden <audio ref={audioRef} loop>.
 */
export function useArcAlarm(serverIp, sensors) {
  const audioRef = useRef(null);
  const unlockedRef = useRef(false);
  const [selectedSound, setSelectedSound] = useState(NO_SOUND.value);

  useEffect(() => {
    if (!serverIp) return;
    let cancelled = false;
    const fetchSoundPreference = async () => {
      try {
        const response = await fetch(`http://${serverIp}:8000/sound-preference/`);
        if (!response.ok) throw new Error("Failed to fetch preference");
        const data = await response.json();
        if (cancelled) return;
        const resolved = resolveSoundFromPreference(data?.selected_sound_file);
        setSelectedSound(resolved.value);
      } catch (error) {
        console.error("Error fetching sound preference:", error);
        if (!cancelled) setSelectedSound(NO_SOUND.value);
      }
    };
    fetchSoundPreference();
    return () => {
      cancelled = true;
    };
  }, [serverIp]);

  // Reload source when preference changes
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;
    audioElement.pause();
    audioElement.load();
  }, [selectedSound]);

  // Browser autoplay policy: unlock after first user gesture
  useEffect(() => {
    const unlock = () => {
      if (unlockedRef.current) return;
      unlockedRef.current = true;
      const audioElement = audioRef.current;
      if (!audioElement) return;
      const prevMuted = audioElement.muted;
      audioElement.muted = true;
      const p = audioElement.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          audioElement.pause();
          audioElement.currentTime = 0;
          audioElement.muted = prevMuted;
        }).catch(() => {
          audioElement.muted = prevMuted;
        });
      } else {
        audioElement.muted = prevMuted;
      }
    };
    window.addEventListener("pointerdown", unlock, { once: true, capture: true });
    window.addEventListener("keydown", unlock, { once: true, capture: true });
    return () => {
      window.removeEventListener("pointerdown", unlock, { capture: true });
      window.removeEventListener("keydown", unlock, { capture: true });
    };
  }, []);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;
    if (!selectedSound) {
      audioElement.pause();
      audioElement.currentTime = 0;
      return;
    }

    if (shouldPlayArcAlarm(sensors)) {
      const playPromise = audioElement.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          if (error?.name !== "AbortError") console.error("Arc alarm playback error:", error);
        });
      }
    } else {
      audioElement.pause();
      audioElement.currentTime = 0;
    }
  }, [sensors, selectedSound]);

  return { audioRef, selectedSound };
}
